import React, { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Package, Clock, Mail, Sparkles, Flower, FlaskConical, Moon, Copy, Check } from 'lucide-react';
import { OrderTimeline } from '../components/OrderTimeline';
import { toast } from 'sonner';
import { trackPurchase } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getOrderTypeLabel = (orderType, quantity) => {
  switch (orderType) {
    case 'stems':
      return `${quantity} ${quantity === 1 ? 'stem' : 'stems'}`;
    case 'subscription-weekly':
      return 'Weekly Subscription';
    case 'subscription-monthly':
      return 'Monthly Subscription';
    default:
      return `${quantity} stems`;
  }
};

const getEstimatedDelivery = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export const Confirmation = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get('orderId');
  const [order, setOrder] = useState(location.state?.order || null);
  const [fetchingOrder, setFetchingOrder] = useState(!location.state?.order && !!orderIdFromUrl);
  const [productsMap, setProductsMap] = useState({});
  const [copied, setCopied] = useState(false);

  const handleCopyId = async () => {
    if (!order?.id) return;
    try {
      await navigator.clipboard.writeText(order.id);
      setCopied(true);
      toast.success('Order ID copied', { description: 'Paste it into the Track Order page anytime.' });
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error('Could not copy', { description: 'Please select and copy manually.' });
    }
  };

  useEffect(() => {
    axios.get(`${API}/products`).then(res => {
      const map = {};
      res.data.forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    }).catch(() => {});
  }, []);

  // Fire GA4 purchase event exactly once per order id
  useEffect(() => {
    if (!order?.id) return;
    const firedKey = `ffl_purchase_tracked_${order.id}`;
    if (sessionStorage.getItem(firedKey)) return;
    trackPurchase(order);
    sessionStorage.setItem(firedKey, '1');
  }, [order?.id]);

  // Fallback: fetch order by ID from URL if state was lost (browser refresh)
  useEffect(() => {
    if (!order && orderIdFromUrl) {
      setFetchingOrder(true);
      axios.get(`${API}/orders/${orderIdFromUrl}`)
        .then(res => setOrder(res.data))
        .catch(err => console.error('[Confirmation] Failed to fetch order:', err))
        .finally(() => setFetchingOrder(false));
    }
  }, [order, orderIdFromUrl]);

  if (fetchingOrder) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center pt-24">
        <p className="text-brand-green-dark font-body">Loading your order...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 flex items-center justify-center fade-in" data-testid="confirmation-empty-state">
        <div className="max-w-lg w-full text-center">
          <div className="mb-8 flex justify-center">
            <img
              src="/logo-transparent.png"
              alt="FarmFreshLily"
              className="h-24 w-auto object-contain"
              style={{ mixBlendMode: 'multiply', background: 'transparent' }}
            />
          </div>

          <div className="bg-brand-blush/30 gold-border-glow p-10 rounded-sm">
            <p className="uppercase tracking-[0.3em] text-xs font-bold text-brand-gold mb-4">
              Nothing to Show
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-brand-green-dark mb-4">
              No order found
            </h1>
            <p className="font-body text-base text-brand-green-dark/70 mb-8 leading-relaxed">
              This confirmation link is empty. Perhaps you refreshed the page or arrived here directly.
              Explore our A-Grade oriental lilies and start a new order.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/products"
                className="inline-block bg-brand-green text-brand-cream px-10 py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
                data-testid="empty-continue-shopping-btn"
              >
                Shop Lilies
              </Link>
              <Link
                to="/"
                className="inline-block border border-brand-green text-brand-green-dark px-10 py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-green hover:text-brand-cream smooth-transition"
                data-testid="empty-home-btn"
              >
                Back to Home
              </Link>
            </div>
          </div>

          <p className="font-body text-xs text-brand-green-dark/50 mt-6 italic">
            "Cultivated with care, delivered with love."
          </p>
        </div>
      </div>
    );
  }

  const shortId = order.id.substring(0, 8).toUpperCase();
  const deliveryDate = getEstimatedDelivery();

  return (
    <div className="min-h-screen bg-brand-cream pt-24 pb-16 px-6 fade-in" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto">
        {/* Celebration header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-brand-green mb-6 shadow-lg">
            <CheckCircle className="w-14 h-14 text-brand-gold" strokeWidth={1.5} />
          </div>
          <p className="uppercase tracking-[0.3em] text-xs font-bold text-brand-gold mb-4">
            Thank You
          </p>
          <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight text-brand-green-dark mb-4">
            Your Order is <span className="italic text-brand-green">Confirmed</span>
          </h1>
          <p className="font-body text-base text-brand-green-dark/80 max-w-lg mx-auto">
            Your A-Grade oriental lilies are being handpicked at our farm and prepared for delivery.
          </p>
        </div>

        {/* Order ID banner */}
        <div className="bg-brand-blush/40 gold-border-glow p-6 rounded-sm mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-1">Order ID</p>
              <p className="font-heading text-2xl text-brand-green-dark" data-testid="order-id">#{shortId}</p>
            </div>
            <div className="text-left sm:text-right">
              <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-1">Total Paid</p>
              <p className="font-heading text-3xl text-brand-green-dark" data-testid="order-total">₹{order.totalAmount}</p>
            </div>
          </div>

          {/* Full ID for tracking — customers need this exact string */}
          <div className="mt-5 pt-5 border-t border-brand-gold/30">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-2">
              Full Order ID (for tracking)
            </p>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-brand-cream border border-brand-gold/40 rounded-sm p-3">
              <code
                className="flex-1 font-mono text-xs sm:text-sm text-brand-green-dark break-all select-all"
                data-testid="order-id-full"
              >
                {order.id}
              </code>
              <button
                type="button"
                onClick={handleCopyId}
                className="inline-flex items-center justify-center gap-2 bg-brand-green text-brand-cream px-4 py-2 rounded-sm font-body uppercase tracking-[0.15em] text-xs font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition flex-shrink-0"
                data-testid="copy-order-id-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" strokeWidth={2} /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Copy
                  </>
                )}
              </button>
            </div>
            <p className="font-body text-xs text-brand-green-dark/60 mt-2">
              Save this ID — you&apos;ll need it to track your order on the <Link to={`/track-order?orderId=${order.id}`} className="underline hover:text-brand-gold">Track Order</Link> page.
            </p>
          </div>
        </div>

        {/* Order Status Timeline */}
        <div className="bg-brand-cream gold-border-glow p-8 rounded-sm mb-8">
          <div className="flex items-center justify-between mb-6">
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold">
              Order Status
            </p>
            <Link
              to={`/track-order?orderId=${order.id}`}
              className="font-body text-xs uppercase tracking-wider text-brand-green hover:text-brand-gold smooth-transition"
              data-testid="track-order-detail-link"
            >
              Track Order →
            </Link>
          </div>
          <OrderTimeline status={order.status || 'Order Received'} />
        </div>

        {/* Items ordered */}
        <div className="bg-brand-cream gold-border-glow p-8 rounded-sm mb-8">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-6">
            Items Ordered
          </p>
          <div className="space-y-4" data-testid="order-items">
            {order.items.map((item, idx) => {
              const product = productsMap[item.productId] || {};
              return (
                <div key={`${item.productId}-${item.orderType}`} className="flex justify-between items-start pb-4 border-b border-brand-gold/20 last:border-b-0" data-testid={`order-item-${idx}`}>
                  <div>
                    <p className="font-heading text-lg text-brand-green-dark">
                      {product.name || 'Oriental Lily'}
                    </p>
                    {product.variety && (
                      <p className="font-body text-xs italic text-brand-green-dark/60 mt-1">
                        {product.variety}
                      </p>
                    )}
                    <p className="font-body text-xs text-brand-gold uppercase tracking-wider mt-2">
                      {getOrderTypeLabel(item.orderType, item.quantity)}
                    </p>
                  </div>
                  <p className="font-body text-lg text-brand-green-dark">
                    ₹{item.price * item.quantity}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Delivery info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-brand-sage/25 gold-border-glow p-6 rounded-sm">
            <Clock className="w-8 h-8 text-brand-gold mb-3" strokeWidth={1.5} />
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-2">
              Estimated Delivery
            </p>
            <p className="font-heading text-xl text-brand-green-dark mb-1">{deliveryDate}</p>
            <p className="font-body text-xs text-brand-green-dark/60">Within 2 business days · Hyderabad</p>
          </div>

          <div className="bg-brand-blush/30 gold-border-glow p-6 rounded-sm">
            <Package className="w-8 h-8 text-brand-gold mb-3" strokeWidth={1.5} />
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-2">
              Delivery Address
            </p>
            <p className="font-body text-sm text-brand-green-dark leading-relaxed">
              {order.customerName}<br />
              {order.address}<br />
              {order.city}, {order.pincode}<br />
              <span className="text-brand-green-dark/60">{order.phone}</span>
            </p>
          </div>
        </div>

        {/* Email confirmation notice */}
        <div className="bg-brand-cream gold-border-glow p-6 rounded-sm mb-8 flex items-start gap-4">
          <Mail className="w-5 h-5 text-brand-gold flex-shrink-0 mt-1" strokeWidth={1.5} />
          <div>
            <p className="font-body text-brand-green-dark font-bold mb-1">Confirmation Email Sent</p>
            <p className="font-body text-sm text-brand-green-dark/70">
              A detailed order confirmation has been sent to <strong className="text-brand-green">{order.email}</strong>.
              Please check your inbox (and spam folder).
            </p>
          </div>
        </div>

        {/* Brand promise */}
        <div className="lily-story-bg p-10 rounded-sm mb-8 text-center">
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">Our Promise</p>
          <p className="font-body text-brand-cream leading-relaxed text-lg mb-6 italic">
            "Cultivated with care, delivered with love."
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <div className="flex items-center gap-2 bg-brand-cream/10 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Sparkles className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-cream">A-Grade Quality</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/10 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Flower className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-cream">Vase Ready</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/10 backdrop-blur-sm px-4 py-2 rounded-sm">
              <FlaskConical className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-cream">Beautiful Aroma</span>
            </div>
            <div className="flex items-center gap-2 bg-brand-cream/10 backdrop-blur-sm px-4 py-2 rounded-sm">
              <Moon className="w-4 h-4 text-brand-gold" strokeWidth={1.5} />
              <span className="font-body text-sm text-brand-cream">Evening Ready</span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link
            to="/products"
            className="inline-block bg-brand-green text-brand-cream px-12 py-4 rounded-sm font-body uppercase tracking-[0.2em] text-sm font-bold hover:bg-brand-gold hover:text-brand-green-dark smooth-transition"
            data-testid="continue-shopping-btn"
          >
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};
